import { computed, ComputedRef, Ref, ref, watch } from "vue"
import { MediaUpload } from "features/media/media.types"
import { Post } from "suus-api"
import { GalleryMedium } from "features/gallery/gallery.types"
import {
  addGalleryMediaToCreatedPost,
  addGalleryMediaToUpdatedPost,
} from "features/posts/form/gallery/gallery.functions"
import { DeletePostFunction } from "features/posts/form/post-form.types"
import { getGalleryMedia } from "features/posts/gallery/gallery.functions"

export const useGalleryAddon = (
  postRef: Ref<Post>,
  deletePost: DeletePostFunction
) => {
  const galleryUploads: Ref<MediaUpload[]> = ref([])

  const setGalleryUploads = (newGalleryUploads: MediaUpload[]) => {
    galleryUploads.value = newGalleryUploads
  }

  const resetFunction = () => {
    galleryUploads.value = []
  }

  const galleryCount = computed(() => {
    return existingGalleryMediaCount.value + galleryUploads.value.length
  })

  const existingGalleryMedia: Ref<GalleryMedium[]> = ref([])
  const existingGalleryMediaCount = ref(postRef.value?.event_media_count ?? 0)
  const existingGalleryMediaLoading = ref(false)

  const loadAllExistingGalleryMedia = async () => {
    existingGalleryMediaLoading.value = true
    let notYetLoadedMediaCount = existingGalleryMediaCount.value
    while (notYetLoadedMediaCount > 0) {
      const offset = existingGalleryMediaCount.value - notYetLoadedMediaCount
      const loadedMedia = await getGalleryMedia(postRef.value.id, offset, 20)
      existingGalleryMedia.value = [
        ...existingGalleryMedia.value,
        ...loadedMedia,
      ]
      notYetLoadedMediaCount -= loadedMedia.length
    }
    existingGalleryMediaLoading.value = false
  }

  const createGalleryMedia = (createdPost: Post) =>
    addGalleryMediaToCreatedPost(createdPost, deletePost, galleryUploads.value)

  const updateGalleryMedia = (updatedPost: Post) =>
    addGalleryMediaToUpdatedPost(
      updatedPost,
      galleryUploads.value,
      existingGalleryMedia.value
    )

  const removeExistingGalleryMedia = (mediumId: number) => {
    existingGalleryMedia.value = existingGalleryMedia.value.filter(
      (medium) => medium.id !== mediumId
    )
    existingGalleryMediaCount.value = existingGalleryMedia.value.length
  }

  return {
    setGalleryUploads,
    galleryCount,
    existingGalleryMedia,
    resetFunction,
    createGalleryMedia,
    updateGalleryMedia,
    removeExistingGalleryMedia,
    existingGalleryMediaLoading,
    loadAllExistingGalleryMedia,
  }
}
